import { useState, useCallback, useRef } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { v4 as uuidv4 } from "uuid";
import ReactPlayer from "react-player";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Download, GripVertical, Plus, Check, Globe, Loader2, AlertCircle, Play } from "lucide-react";

// --- Timestamp parsing ---

function timestampToSeconds(ts: string): number {
  const parts = ts.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function parseAndNormalizeTimestamp(raw: string): { normalized: string; error: string | null } {
  const trimmed = raw.trim();
  if (trimmed === "") return { normalized: "", error: null };
  if (/[^0-9:]/.test(trimmed)) return { normalized: trimmed, error: "Use m:ss or h:mm:ss" };
  if (trimmed.includes("::") || trimmed.endsWith(":")) return { normalized: trimmed, error: "Use m:ss or h:mm:ss" };

  if (trimmed.includes(":")) {
    const parts = trimmed.split(":");
    if (parts.length === 2) {
      const minStr = parts[0];
      const secStr = parts[1];
      if (secStr.length !== 2) return { normalized: trimmed, error: "Seconds must be 2 digits" };
      const minutes = minStr === "" ? 0 : parseInt(minStr, 10);
      const seconds = parseInt(secStr, 10);
      if (isNaN(minutes) || isNaN(seconds)) return { normalized: trimmed, error: "Use m:ss or h:mm:ss" };
      if (seconds < 0 || seconds > 59) return { normalized: trimmed, error: "Seconds must be 0-59" };
      return { normalized: `${minutes}:${String(seconds).padStart(2, "0")}`, error: null };
    }
    if (parts.length === 3) {
      const hourStr = parts[0];
      const minStr = parts[1];
      const secStr = parts[2];
      if (minStr.length !== 2) return { normalized: trimmed, error: "Minutes must be 2 digits with hours" };
      if (secStr.length !== 2) return { normalized: trimmed, error: "Seconds must be 2 digits" };
      const hours = parseInt(hourStr, 10);
      const minutes = parseInt(minStr, 10);
      const seconds = parseInt(secStr, 10);
      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return { normalized: trimmed, error: "Use m:ss or h:mm:ss" };
      if (seconds > 59) return { normalized: trimmed, error: "Seconds must be 0-59" };
      if (minutes > 59) return { normalized: trimmed, error: "Minutes must be 0-59 with hours" };
      return { normalized: `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`, error: null };
    }
    return { normalized: trimmed, error: "Use m:ss or h:mm:ss" };
  }

  const seconds = parseInt(trimmed, 10);
  if (isNaN(seconds) || seconds < 0) return { normalized: trimmed, error: "Use m:ss or h:mm:ss" };
  return { normalized: `0:${String(seconds).padStart(2, "0")}`, error: null };
}

// --- Types ---

interface KeyMomentUI {
  clientId: string;
  timestamp: string;
  label: string;
  timestampError?: string;
  timestampEnd: string;
  timestampEndError?: string;
  endMode: "custom" | "30s" | "60s";
}

interface SubmittedTimestamp {
  timestamp: string;
  label: string;
  durationLabel: string;
}

interface SubmittedClip {
  id: number;
  timestamp: string;
  timestampEnd: string;
  label: string;
  status: "queued" | "processing" | "done" | "failed";
  durationLabel: string;
}

interface RecordingData {
  type: "private_recording";
  title: string;
  recordingDate: string;
  videoUrl: string;
  permissions: {
    enableVideoDownload: boolean;
    enableAudioDownload: boolean;
    enableTimestampDownloads: boolean;
  };
  timestamps: Array<{
    startTime: string;
    endTime: string;
    durationType: string;
    trackTitle: string;
    sortOrder: number;
  }>;
  storageKey: string;
}

interface ClipData {
  id: number;
  startTime: string;
  endTime: string | null;
  durationType: string;
  trackTitle: string;
  status: "queued" | "processing" | "done" | "failed";
  durationSeconds: number | null;
}

function getClipStatusBadge(status: string) {
  switch (status) {
    case "queued":
      return <Badge variant="secondary" className="bg-gray-600 text-gray-200">Queued</Badge>;
    case "processing":
      return (
        <Badge variant="secondary" className="bg-amber-600/20 text-amber-400 border-amber-600/30">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          Processing
        </Badge>
      );
    case "done":
      return <Badge variant="secondary" className="bg-green-600/20 text-green-400 border-green-600/30">Done</Badge>;
    case "failed":
      return <Badge variant="destructive">Failed</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

// --- Sortable row ---

function SortableTimestampRow({
  km,
  index,
  onTimestampChange,
  onTimestampBlur,
  onTimestampEndChange,
  onTimestampEndBlur,
  onEndModeChange,
  onLabelChange,
}: {
  km: KeyMomentUI;
  index: number;
  onTimestampChange: (clientId: string, value: string) => void;
  onTimestampBlur: (clientId: string) => void;
  onTimestampEndChange: (clientId: string, value: string) => void;
  onTimestampEndBlur: (clientId: string) => void;
  onEndModeChange: (clientId: string, mode: "custom" | "30s" | "60s") => void;
  onLabelChange: (clientId: string, value: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: km.clientId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="space-y-2">
      <div className="flex items-center gap-2">
        <button type="button" className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-300 touch-none" {...attributes} {...listeners}>
          <GripVertical className="h-4 w-4" />
        </button>
        <h4 className="text-sm font-medium text-gray-300">Timestamp {index + 1}</h4>
      </div>
      <div className="pl-6 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-shrink-0">
            <label htmlFor={`ts-${km.clientId}`} className="block text-xs text-gray-400 mb-1">Start</label>
            <Input
              id={`ts-${km.clientId}`}
              type="text"
              value={km.timestamp}
              onChange={(e) => onTimestampChange(km.clientId, e.target.value.replace(/[^0-9:]/g, ""))}
              onBlur={() => onTimestampBlur(km.clientId)}
              placeholder="h:mm:ss"
              className={`bg-[#1a1a1a] border text-white placeholder:text-gray-500 w-28 text-sm tabular-nums rounded-md ${km.timestampError ? "border-red-500" : "border-white/10"}`}
              maxLength={10}
            />
            {km.timestampError && <p className="text-xs text-red-400 mt-1">{km.timestampError}</p>}
          </div>

          <div className="flex-shrink-0">
            <label className="block text-xs text-gray-400 mb-1">Duration</label>
            <div className="flex items-center gap-1.5 h-10 bg-[#1a1a1a] border border-white/10 rounded-md px-1">
              {(["30s", "60s", "custom"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => onEndModeChange(km.clientId, mode)}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${km.endMode === mode ? "bg-white text-black" : "text-white/40 hover:text-white/70"}`}
                >
                  {mode === "custom" ? "Custom" : mode}
                </button>
              ))}
            </div>
          </div>

          {km.endMode === "custom" && (
            <div className="flex-shrink-0">
              <label htmlFor={`ts-end-${km.clientId}`} className="block text-xs text-gray-400 mb-1">End</label>
              <Input
                id={`ts-end-${km.clientId}`}
                type="text"
                value={km.timestampEnd}
                onChange={(e) => onTimestampEndChange(km.clientId, e.target.value.replace(/[^0-9:]/g, ""))}
                onBlur={() => onTimestampEndBlur(km.clientId)}
                placeholder="h:mm:ss"
                className={`bg-[#1a1a1a] border text-white placeholder:text-gray-500 w-28 text-sm tabular-nums rounded-md ${km.timestampEndError ? "border-red-500" : "border-white/10"}`}
                maxLength={10}
              />
            </div>
          )}

          <div className="flex-grow">
            <label htmlFor={`track-${km.clientId}`} className="block text-xs text-gray-400 mb-1">Track</label>
            <Input
              id={`track-${km.clientId}`}
              type="text"
              value={km.label}
              onChange={(e) => onLabelChange(km.clientId, e.target.value)}
              placeholder="Track name or description..."
              className="bg-[#1a1a1a] border border-white/10 text-white placeholder:text-gray-500 w-full"
            />
          </div>
        </div>
        {km.timestampEndError && (
          <p className="text-xs text-red-400">{km.timestampEndError}</p>
        )}
      </div>
    </div>
  );
}

// --- Page ---

export default function PrivateRecordingViewerPage() {
  const { token } = useParams<{ token: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const playerRef = useRef<any>(null);
  const [isVideoStarted, setIsVideoStarted] = useState(false);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  // Fetch recording data via share token
  const { data: recording, isLoading, isError } = useQuery<RecordingData>({
    queryKey: ["share", token],
    queryFn: async () => {
      const res = await fetch(`/api/share/${token}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!token,
  });

  // Fetch clips data via share token (only when timestamp downloads enabled)
  const { data: clipsData } = useQuery<ClipData[]>({
    queryKey: ["share", token, "clips"],
    queryFn: async () => {
      const res = await fetch(`/api/share/${token}/clips`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch clips");
      const data = await res.json();
      return data.clips ?? data;
    },
    enabled: !!token && !!recording?.permissions?.enableTimestampDownloads,
    refetchInterval: (query) => {
      const clips = query.state.data;
      if (!clips) return false;
      const hasProcessing = clips.some((c: ClipData) => c.status === "queued" || c.status === "processing");
      return hasProcessing ? 5000 : false;
    },
  });

  // Submit timestamps mutation
  const submitTimestampsMutation = useMutation({
    mutationFn: async (timestamps: Array<{ startTime: string; endTime: string; durationType: string; trackTitle: string; sortOrder: number }>) => {
      const res = await fetch(`/api/share/${token}/timestamps`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ timestamps }),
      });
      if (!res.ok) throw new Error("Failed to submit timestamps");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["share", token] });
      queryClient.invalidateQueries({ queryKey: ["share", token, "clips"] });
    },
  });

  const INITIAL_VISIBLE = 2;
  const MAX_TIMESTAMPS = 20;
  const ADD_INCREMENT = 4;
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  // Tab toggle (only relevant when timestampDownloadsEnabled)
  const tabRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<"timestamps" | "clips">("timestamps");
  const switchTab = (tab: "timestamps" | "clips") => {
    setActiveTab(tab);
    requestAnimationFrame(() => {
      tabRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    });
  };

  // Previously submitted timestamps (from API)
  const submittedTimestamps: SubmittedTimestamp[] = (recording?.timestamps || []).map((ts) => ({
    timestamp: ts.startTime,
    label: ts.trackTitle,
    durationLabel: ts.durationType,
  }));

  // Clips from API — map API fields to UI fields
  const submittedClips: SubmittedClip[] = (clipsData || []).map((c) => ({
    id: c.id,
    timestamp: c.startTime,
    timestampEnd: c.endTime ?? "",
    label: c.trackTitle,
    status: c.status,
    durationLabel: c.durationType === "30s" || c.durationType === "60s" ? c.durationType : "Custom",
  }));

  // Editable timestamp rows
  const [keyMomentsUi, setKeyMomentsUi] = useState<KeyMomentUI[]>(() =>
    Array.from({ length: MAX_TIMESTAMPS }, () => ({
      clientId: uuidv4(),
      timestamp: "",
      label: "",
      timestampEnd: "",
      endMode: "60s" as const,
    }))
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setKeyMomentsUi((items) => {
        const oldIndex = items.findIndex((i) => i.clientId === active.id);
        const newIndex = items.findIndex((i) => i.clientId === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  const handleTimestampChange = useCallback((clientId: string, value: string) => {
    setKeyMomentsUi((prev) => prev.map((m) => (m.clientId === clientId ? { ...m, timestamp: value, timestampError: undefined } : m)));
  }, []);

  const handleTimestampBlur = useCallback((clientId: string) => {
    setKeyMomentsUi((prev) =>
      prev.map((m) => {
        if (m.clientId !== clientId || !m.timestamp.trim()) return m;
        const { normalized, error } = parseAndNormalizeTimestamp(m.timestamp);
        return { ...m, timestamp: normalized, timestampError: error || undefined };
      })
    );
  }, []);

  const handleLabelChange = useCallback((clientId: string, value: string) => {
    setKeyMomentsUi((prev) => prev.map((m) => (m.clientId === clientId ? { ...m, label: value } : m)));
  }, []);

  const handleTimestampEndChange = useCallback((clientId: string, value: string) => {
    setKeyMomentsUi((prev) => prev.map((m) => (m.clientId === clientId ? { ...m, timestampEnd: value, timestampEndError: undefined } : m)));
  }, []);

  const handleTimestampEndBlur = useCallback((clientId: string) => {
    setKeyMomentsUi((prev) =>
      prev.map((m) => {
        if (m.clientId !== clientId || m.endMode !== "custom" || !m.timestampEnd.trim()) return m;
        const { normalized, error } = parseAndNormalizeTimestamp(m.timestampEnd);
        if (error) return { ...m, timestampEnd: normalized, timestampEndError: error };
        if (normalized && m.timestamp.trim()) {
          const startSec = timestampToSeconds(m.timestamp);
          const endSec = timestampToSeconds(normalized);
          if (endSec <= startSec) {
            return { ...m, timestampEnd: normalized, timestampEndError: "End time must be after start time" };
          }
          if (endSec - startSec > 60) {
            return { ...m, timestampEnd: normalized, timestampEndError: "Clip cannot exceed 60 seconds" };
          }
        }
        return { ...m, timestampEnd: normalized, timestampEndError: undefined };
      })
    );
  }, []);

  const handleEndModeChange = useCallback((clientId: string, mode: "custom" | "30s" | "60s") => {
    setKeyMomentsUi((prev) =>
      prev.map((m) => (m.clientId === clientId ? { ...m, endMode: mode, timestampEnd: mode === "custom" ? m.timestampEnd : "", timestampEndError: undefined } : m))
    );
  }, []);

  const handleSubmit = () => {
    const filled = keyMomentsUi.slice(0, visibleCount).filter((km) => km.label.trim() !== "");
    if (filled.length === 0) {
      toast({ title: "Add at least one timestamp before submitting" });
      return;
    }

    let hasErrors = false;
    const validated = keyMomentsUi.map((km) => {
      if (km.label.trim() === "") return km;
      const { normalized, error } = parseAndNormalizeTimestamp(km.timestamp);
      if (error || !normalized) hasErrors = true;
      let endError: string | undefined;
      let normalizedEnd = km.timestampEnd;
      if (km.endMode === "custom") {
        if (!km.timestampEnd.trim()) {
          endError = "End time is required for custom clips";
          hasErrors = true;
        } else {
          const endResult = parseAndNormalizeTimestamp(km.timestampEnd);
          normalizedEnd = endResult.normalized;
          if (endResult.error) {
            endError = endResult.error;
            hasErrors = true;
          } else if (normalized && normalizedEnd) {
            const startSec = timestampToSeconds(normalized);
            const endSec = timestampToSeconds(normalizedEnd);
            if (endSec <= startSec) {
              endError = "End time must be after start time";
              hasErrors = true;
            } else if (endSec - startSec > 60) {
              endError = "Clip cannot exceed 60 seconds";
              hasErrors = true;
            }
          }
        }
      }
      return { ...km, timestamp: normalized, timestampError: error || (!normalized ? "Timestamp is required" : undefined), timestampEnd: normalizedEnd, timestampEndError: endError };
    });
    if (hasErrors) {
      setKeyMomentsUi(validated);
      return;
    }

    // Build payload for API
    const existingCount = submittedTimestamps.length;
    const timestamps = filled.map((km, idx) => ({
      startTime: km.timestamp,
      endTime: km.endMode === "custom" ? km.timestampEnd : "",
      durationType: km.endMode === "30s" ? "30s" : km.endMode === "60s" ? "60s" : "custom",
      trackTitle: km.label,
      sortOrder: existingCount + idx + 1,
    }));

    submitTimestampsMutation.mutate(timestamps, {
      onSuccess: () => {
        // Switch to clips tab when timestamp downloads are enabled
        if (recording?.permissions?.enableTimestampDownloads) {
          switchTab("clips");
        }

        // Reset the form with fresh empty rows
        setKeyMomentsUi(
          Array.from({ length: MAX_TIMESTAMPS }, () => ({
            clientId: uuidv4(),
            timestamp: "",
            label: "",
            timestampEnd: "",
            endMode: "60s" as const,
          }))
        );
        setVisibleCount(INITIAL_VISIBLE);

        toast({ title: `${timestamps.length} timestamp${timestamps.length > 1 ? "s" : ""} submitted` });
      },
      onError: () => {
        toast({ title: "Failed to submit timestamps. Please try again." });
      },
    });
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-clubhouse-black text-white flex flex-col">
        <header className="border-b border-white/[0.06]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-2">
            <Globe className="h-4 w-4 text-white/60" />
            <span className="text-[14px] font-medium tracking-widest uppercase text-white/80">Clubhouse FM</span>
          </div>
        </header>
        <div className="flex-grow flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-white/30" />
        </div>
      </div>
    );
  }

  if (isError || !recording) {
    return (
      <div className="min-h-screen bg-clubhouse-black text-white flex flex-col">
        <header className="border-b border-white/[0.06]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-2">
            <Globe className="h-4 w-4 text-white/60" />
            <span className="text-[14px] font-medium tracking-widest uppercase text-white/80">Clubhouse FM</span>
          </div>
        </header>
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="text-white/40 text-[16px] mb-2">Recording not found</p>
            <p className="text-white/20 text-[14px]">This link may have expired or the recording has been removed.</p>
          </div>
        </div>
      </div>
    );
  }

  const showDownloads = recording.permissions.enableVideoDownload || recording.permissions.enableAudioDownload;

  return (
    <div className="min-h-screen bg-clubhouse-black text-white flex flex-col">
      {/* Header — branded but minimal, no login/nav */}
      <header className="border-b border-white/[0.06] bg-clubhouse-black/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center">
          <span className="text-white/80 text-[14px] font-medium tracking-widest uppercase inline-flex items-center gap-2">
            <Globe className="h-4 w-4 shrink-0" />
            Clubhouse FM
          </span>
        </div>
      </header>

      <main className="flex-grow">
        {/* Title + date */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-2">
          <h1 className="text-[24px] sm:text-[32px] font-semibold tracking-tight">{recording.title}</h1>
          <p className="text-[14px] text-white/30 mt-1.5">{recording.recordingDate}</p>
        </div>

        {/* Video player */}
        <section className="max-w-5xl mx-auto w-full py-5 px-4 sm:px-6">
          <div className="relative aspect-video rounded-xl overflow-hidden shadow-2xl bg-black border border-white/[0.06]">
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
              <ReactPlayer
                ref={playerRef}
                url={recording.videoUrl}
                controls
                width="100%"
                height="100%"
                playing={false}
                onPlay={() => setIsVideoStarted(true)}
                onDuration={(d) => setVideoDuration(d)}
                onProgress={({ playedSeconds }) => setVideoCurrentTime(playedSeconds)}
                config={{ file: { attributes: { controlsList: "nodownload", onContextMenu: (e: React.MouseEvent) => e.preventDefault(), crossOrigin: "anonymous" }, forceVideo: true } }}
              />
            </div>
            {isVideoStarted && (
              <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 pointer-events-none z-10">
                <span className="text-[20px] text-white tabular-nums font-medium">
                  {formatTime(videoCurrentTime)} <span className="text-white/40">/ {formatTime(videoDuration)}</span>
                </span>
              </div>
            )}
            {/* Placeholder when no video URL */}
            {!recording.videoUrl && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-white/20 text-[15px]">Video preview will appear here</p>
              </div>
            )}
          </div>
        </section>

        {/* Download links — only if enabled by the admin */}
        {showDownloads && (
          <section className="max-w-5xl mx-auto w-full pb-4 px-4 sm:px-6">
            <div className="flex items-center gap-4">
              {recording.permissions.enableVideoDownload && (
                <button
                  onClick={() => window.open(`/api/share/${token}/download/video`)}
                  className="flex items-center gap-1.5 text-[13px] text-white/30 hover:text-white/60 transition-colors"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download video
                </button>
              )}
              {recording.permissions.enableVideoDownload && recording.permissions.enableAudioDownload && (
                <span className="text-white/10">|</span>
              )}
              {recording.permissions.enableAudioDownload && (
                <button
                  onClick={() => window.open(`/api/share/${token}/download/audio`)}
                  className="flex items-center gap-1.5 text-[13px] text-white/30 hover:text-white/60 transition-colors"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download audio
                </button>
              )}
            </div>
          </section>
        )}

        {/* Tab toggle — only when timestamp downloads are enabled */}
        {recording.permissions.enableTimestampDownloads ? (
          <section className="max-w-5xl mx-auto w-full pb-16 px-4 sm:px-6">
            {/* Segmented toggle */}
            <div ref={tabRef} className="flex items-center justify-center mb-6">
              <div className="inline-flex bg-white/[0.04] border border-white/[0.08] rounded-full p-1">
                <button
                  onClick={() => switchTab("timestamps")}
                  className={`px-5 py-2 rounded-full text-[14px] font-medium transition-colors ${
                    activeTab === "timestamps"
                      ? "bg-white text-black"
                      : "text-white/40 hover:text-white/70"
                  }`}
                >
                  Add Timestamps
                </button>
                <button
                  onClick={() => switchTab("clips")}
                  className={`px-5 py-2 rounded-full text-[14px] font-medium transition-colors ${
                    activeTab === "clips"
                      ? "bg-white text-black"
                      : "text-white/40 hover:text-white/70"
                  }`}
                >
                  Download Clips{submittedClips.length > 0 ? ` (${submittedClips.length})` : ""}
                </button>
              </div>
            </div>

            {activeTab === "timestamps" ? (
              <>
                {/* Previously submitted timestamps */}
                {submittedTimestamps.length > 0 && (
                  <div className="rounded-2xl bg-emerald-500/[0.04] border border-emerald-500/[0.12] p-6 mb-5">
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="h-6 w-6 rounded-full bg-emerald-500/15 flex items-center justify-center">
                        <Check className="h-3.5 w-3.5 text-emerald-400" />
                      </div>
                      <h3 className="text-[15px] font-medium text-emerald-400/90">
                        {submittedTimestamps.length} timestamp{submittedTimestamps.length > 1 ? "s" : ""} submitted
                      </h3>
                    </div>
                    <div className="space-y-0">
                      {submittedTimestamps.map((ts, idx) => (
                        <div key={idx} className="flex items-center gap-4 py-2.5 border-b border-white/[0.04] last:border-b-0">
                          <span className="text-[12px] text-white/20 w-5 tabular-nums text-right">{idx + 1}</span>
                          <span className="font-mono text-[14px] text-white/60 tabular-nums w-20">{ts.timestamp}</span>
                          <span className="text-[12px] text-white/25 w-10">{ts.durationLabel}</span>
                          <span className="text-[14px] text-white/80 truncate">{ts.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timestamp form */}
                <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
                  <h3 className="text-[18px] font-semibold mb-1">
                    {submittedTimestamps.length > 0 ? "Add more timestamps" : "Add timestamps"}
                  </h3>
                  <p className="text-[14px] text-white/35 mb-6">
                    {submittedTimestamps.length > 0
                      ? "Have more moments to mark? Add them below."
                      : "Mark key moments from this recording. Drag to reorder."}
                  </p>

                  <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
                    <SortableContext items={keyMomentsUi.slice(0, visibleCount).map((km) => km.clientId)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-4">
                        {keyMomentsUi.slice(0, visibleCount).map((km, index) => (
                          <SortableTimestampRow
                            key={km.clientId}
                            km={km}
                            index={submittedTimestamps.length + index}
                            onTimestampChange={handleTimestampChange}
                            onTimestampBlur={handleTimestampBlur}
                            onTimestampEndChange={handleTimestampEndChange}
                            onTimestampEndBlur={handleTimestampEndBlur}
                            onEndModeChange={handleEndModeChange}
                            onLabelChange={handleLabelChange}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>

                  {visibleCount < MAX_TIMESTAMPS && (
                    <div className="mt-5">
                      <button
                        type="button"
                        onClick={() => setVisibleCount((prev) => Math.min(prev + ADD_INCREMENT, MAX_TIMESTAMPS))}
                        className="flex items-center gap-2 text-[14px] text-white/40 hover:text-white/70 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        Add more timestamps
                      </button>
                    </div>
                  )}

                  <div className="flex justify-end mt-6">
                    <Button
                      onClick={handleSubmit}
                      disabled={submitTimestampsMutation.isPending}
                      className="bg-white hover:bg-white/90 text-black font-medium px-8 rounded-full"
                    >
                      {submitTimestampsMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Timestamps"
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* --- Download Clips tab --- */
              <div className="space-y-4">
                {submittedClips.length > 0 ? (
                  <>
                    <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 text-[14px] text-center">
                      Your clips are being generated from the recording.
                    </div>

                    <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold">Your Clips</h3>
                          <p className="text-sm text-white/30 mt-0.5">
                            {submittedClips.filter((c) => c.status === "done").length} of {submittedClips.length} clips ready
                          </p>
                        </div>
                        <Button
                          onClick={() => {
                            window.open(`/api/share/${token}/bundle`);
                          }}
                          disabled={submittedClips.filter((c) => c.status === "done").length === 0}
                          className="bg-white hover:bg-white/90 text-black font-medium px-6"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download All ZIP
                        </Button>
                      </div>

                      {/* Clip table */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/[0.08] text-[12px] text-white/30 uppercase tracking-wider">
                              <th className="text-left py-2.5 pr-4">#</th>
                              <th className="text-left py-2.5 pr-4">Start</th>
                              <th className="text-left py-2.5 pr-4">Duration</th>
                              <th className="text-left py-2.5 pr-4">Track</th>
                              <th className="text-left py-2.5 pr-4">Status</th>
                              <th className="text-left py-2.5 pr-4"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {submittedClips.map((clip, idx) => (
                              <tr key={clip.id} className="border-b border-white/[0.04] last:border-b-0">
                                <td className="py-3.5 pr-4 text-white/20 tabular-nums">{idx + 1}</td>
                                <td className="py-3.5 pr-4 font-mono text-white/80 tabular-nums">{clip.timestamp}</td>
                                <td className="py-3.5 pr-4 text-white/40">{clip.durationLabel}</td>
                                <td className="py-3.5 pr-4 text-white/80 max-w-xs truncate">{clip.label}</td>
                                <td className="py-3.5 pr-4">
                                  <div className="flex items-center gap-2">
                                    {getClipStatusBadge(clip.status)}
                                    {clip.status === "failed" && (
                                      <span title="Clip generation failed">
                                        <AlertCircle className="h-4 w-4 text-red-400" />
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-3.5">
                                  {clip.status === "done" && (
                                    <button
                                      onClick={() => toast({ title: `Preview for "${clip.label}"` })}
                                      className="flex items-center gap-1 text-[13px] text-white/30 hover:text-white/60 transition-colors"
                                    >
                                      <Play className="h-3.5 w-3.5" />
                                      Preview
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Add More Clips */}
                      <div className="mt-5 flex justify-center">
                        <button
                          onClick={() => switchTab("timestamps")}
                          className="flex items-center gap-2 text-[14px] text-white/40 hover:text-white/70 transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                          Add More Clips
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Empty state */
                  <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-12">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="h-12 w-12 rounded-full bg-white/[0.04] flex items-center justify-center mb-4">
                        <Download className="h-5 w-5 text-white/20" />
                      </div>
                      <p className="text-[15px] text-white/40 mb-1">No clips yet</p>
                      <p className="text-[13px] text-white/20 mb-5">Submit timestamps to generate downloadable clips from this recording.</p>
                      <Button
                        onClick={() => switchTab("timestamps")}
                        className="bg-white hover:bg-white/90 text-black font-medium px-6"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Timestamps
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        ) : (
          /* No timestamp downloads — simple view without tabs */
          <>
            {/* Previously submitted timestamps */}
            {submittedTimestamps.length > 0 && (
              <section className="max-w-5xl mx-auto w-full pb-5 px-4 sm:px-6">
                <div className="rounded-2xl bg-emerald-500/[0.04] border border-emerald-500/[0.12] p-6">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="h-6 w-6 rounded-full bg-emerald-500/15 flex items-center justify-center">
                      <Check className="h-3.5 w-3.5 text-emerald-400" />
                    </div>
                    <h3 className="text-[15px] font-medium text-emerald-400/90">
                      {submittedTimestamps.length} timestamp{submittedTimestamps.length > 1 ? "s" : ""} submitted
                    </h3>
                  </div>
                  <div className="space-y-0">
                    {submittedTimestamps.map((ts, idx) => (
                      <div key={idx} className="flex items-center gap-4 py-2.5 border-b border-white/[0.04] last:border-b-0">
                        <span className="text-[12px] text-white/20 w-5 tabular-nums text-right">{idx + 1}</span>
                        <span className="font-mono text-[14px] text-white/60 tabular-nums w-20">{ts.timestamp}</span>
                        <span className="text-[12px] text-white/25 w-10">{ts.durationLabel}</span>
                        <span className="text-[14px] text-white/80 truncate">{ts.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Timestamp form */}
            <section className="max-w-5xl mx-auto w-full pb-16 px-4 sm:px-6">
              <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
                <h3 className="text-[18px] font-semibold mb-1">
                  {submittedTimestamps.length > 0 ? "Add more timestamps" : "Add timestamps"}
                </h3>
                <p className="text-[14px] text-white/35 mb-6">
                  {submittedTimestamps.length > 0
                    ? "Have more moments to mark? Add them below."
                    : "Mark key moments from this recording. Drag to reorder."}
                </p>

                <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
                  <SortableContext items={keyMomentsUi.slice(0, visibleCount).map((km) => km.clientId)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-4">
                      {keyMomentsUi.slice(0, visibleCount).map((km, index) => (
                        <SortableTimestampRow
                          key={km.clientId}
                          km={km}
                          index={submittedTimestamps.length + index}
                          onTimestampChange={handleTimestampChange}
                          onTimestampBlur={handleTimestampBlur}
                          onTimestampEndChange={handleTimestampEndChange}
                          onTimestampEndBlur={handleTimestampEndBlur}
                          onEndModeChange={handleEndModeChange}
                          onLabelChange={handleLabelChange}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                {visibleCount < MAX_TIMESTAMPS && (
                  <div className="mt-5">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((prev) => Math.min(prev + ADD_INCREMENT, MAX_TIMESTAMPS))}
                      className="flex items-center gap-2 text-[14px] text-white/40 hover:text-white/70 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add more timestamps
                    </button>
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <Button
                    onClick={handleSubmit}
                    disabled={submitTimestampsMutation.isPending}
                    className="bg-white hover:bg-white/90 text-black font-medium px-8 rounded-full"
                  >
                    {submitTimestampsMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Timestamps"
                    )}
                  </Button>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <span className="text-[13px] text-white/15 tracking-widest uppercase">Clubhouse FM</span>
        </div>
      </footer>
    </div>
  );
}
